import { useQueryClient as useQC } from '@tanstack/react-query';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { toast } from 'sonner';
import { fetchAdminAttachmentTemplate } from '@/api/orders';
import { QueryErrorState } from '@/components/shared/QueryErrorState';
import { useCountryOptions } from '@/hooks/useDropdowns';
import type { FacilityProcessGroupOption } from '@/hooks/useGroupedFacilityProcessOptions';
import { useGroupedFacilityProcessOptions } from '@/hooks/useGroupedFacilityProcessOptions';
import { useTranslator } from '@/hooks/useTranslator';
import { queryClient } from '@/lib/utils/queryClient';
import { AttachmentTemplateForm } from '../../../../-components/AttachmentTemplateForm';
import { useAdminAttachmentTemplate } from '../../../../-hooks/useAdminAttachmentTemplate';
import { useUpdateAdminAttachmentTemplate } from '../../../../-hooks/useUpdateAdminAttachmentTemplate';
import type { AttachmentTemplateFormValues } from '../../../../-types';
import {
  toTemplateFormValues,
  toTemplatePayload,
} from '../../../../-utils/attachmentTemplateTransformers';

export const Route = createFileRoute(
  '/(client)/orders/(pages)/attachment-templates/$id/edit/',
)({
  loader: ({ params }) =>
    queryClient.ensureQueryData({
      queryKey: ['orders', 'adminAttachmentTemplate', params.id],
      queryFn: () => fetchAdminAttachmentTemplate(params.id),
    }),
  component: EditAttachmentTemplatePage,
});

function EditAttachmentTemplatePage() {
  const { id } = Route.useParams();
  const { getTranslation: t } = useTranslator({ page: 'attachmentTemplates' });
  const navigate = useNavigate();
  const qc = useQC();

  const { data, error, refetch } = useAdminAttachmentTemplate(id);
  const mutation = useUpdateAdminAttachmentTemplate(id);
  const { data: fpGroups = [] } = useGroupedFacilityProcessOptions();
  const { data: countryOptions = [] } = useCountryOptions();

  const allFpKeys = (fpGroups as FacilityProcessGroupOption[]).flatMap(g =>
    g.options.map(o => o.key),
  );
  const allCountryCodes = countryOptions.map(c => c.key);

  if (error || !data) {
    return <QueryErrorState error={error} onRetry={() => void refetch()} />;
  }

  const defaultValues: AttachmentTemplateFormValues = toTemplateFormValues(
    data,
    allFpKeys,
    allCountryCodes,
  );

  const handleSubmit = async (values: AttachmentTemplateFormValues) => {
    try {
      const payload = toTemplatePayload(values, allFpKeys, allCountryCodes);
      await mutation.mutateAsync(payload);
      toast.success(t('edit.success'));
      await qc.invalidateQueries({
        queryKey: ['orders', 'adminAttachmentTemplate', id],
      });
      await qc.invalidateQueries({
        queryKey: ['orders', 'adminAttachmentTemplates'],
      });
      void navigate({
        to: '/orders/attachment-templates/$id',
        params: { id },
      });
    } catch (error) {
      toast.error((error as Error).message);
    }
  };

  return (
    <AttachmentTemplateForm
      defaultValues={defaultValues}
      onSubmit={handleSubmit}
      isPending={mutation.isPending}
      submitLabel={t('edit.title')}
      hasOrders={data.hasOrders}
      fpGroups={fpGroups as FacilityProcessGroupOption[]}
      countryOptions={countryOptions}
    />
  );
}
